import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFeaturedMovies } from '@/utils/movie';

const featured = getFeaturedMovies();

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const timerRef = useRef<number | null>(null);

  const goTo = (next: number) => {
    setIndex((next + featured.length) % featured.length);
  };

  useEffect(() => {
    timerRef.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % featured.length);
    }, 6000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, []);

  const pause = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
  };

  const active = featured[index];

  return (
    <section className="relative w-full h-[560px] md:h-[720px] overflow-hidden bg-background-950">
      {featured.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === index ? 'opacity-100' : 'opacity-0'}`}
        >
          <img
            src={movie.backdrop}
            alt={`${movie.title} 影片剧照`}
            title={`${movie.title} 在线观看`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/80 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/30 to-background-950/60"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-center">
        <div key={active.id} className="max-w-2xl animate-fade-up">
          <div className="flex items-center gap-3 mb-4 md:mb-5">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500/15 border border-primary-500/30 text-primary-300 text-xs font-medium">
              <i className="ri-fire-fill"></i>
              本站热映
            </span>
            <span className="text-xs md:text-sm text-foreground-300 font-display tracking-widest">
              FEATURED · {active.year}
            </span>
          </div>

          <h1 className="text-glow text-3xl md:text-6xl font-heading font-black text-foreground-50 leading-tight">
            {active.title}
          </h1>

          <div className="mt-4 md:mt-5 flex items-center flex-wrap gap-x-4 gap-y-2 text-xs md:text-sm text-foreground-300">
            <span className="flex items-center gap-1 text-primary-400 font-semibold">
              <i className="ri-star-fill"></i>
              {active.rating.toFixed(1)}
            </span>
            <span className="hidden sm:flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {active.year}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {active.duration}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-map-pin-line"></i>
              {active.region}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-price-tag-3-line"></i>
              {active.types.join(' · ')}
            </span>
          </div>

          <p className="mt-4 md:mt-6 text-sm md:text-base text-foreground-200 leading-relaxed max-w-xl line-clamp-2 md:line-clamp-3">
            {active.summary}
          </p>

          <div className="mt-6 md:mt-8 flex items-center gap-3">
            <Link
              to={`/movie-detail/${active.id}`}
              className="inline-flex items-center gap-2 px-5 md:px-7 py-3 rounded-md bg-primary-500 text-background-50 text-sm md:text-base font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-fill text-lg"></i>
              立即观看
            </Link>
            <Link
              to="/movie-category"
              className="inline-flex items-center gap-2 px-5 md:px-6 py-3 rounded-md bg-background-800/70 border border-background-700 text-foreground-100 text-sm md:text-base font-medium hover:bg-background-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-grid-line"></i>
              浏览分类
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => {
          pause();
          goTo(index - 1);
        }}
        aria-label="上一部"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full items-center justify-center bg-background-950/50 border border-background-700/70 text-foreground-100 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => {
          pause();
          goTo(index + 1);
        }}
        aria-label="下一部"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full items-center justify-center bg-background-950/50 border border-background-700/70 text-foreground-100 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 md:bottom-8 left-4 md:left-8 z-20 flex items-center gap-4">
        <div className="flex items-center gap-2">
          {featured.map((movie, i) => (
            <button
              key={movie.id}
              type="button"
              onClick={() => {
                pause();
                setIndex(i);
              }}
              aria-label={`切换到 ${movie.title}`}
              className={`h-1 rounded-full transition-all cursor-pointer ${
                i === index ? 'w-8 bg-primary-500' : 'w-4 bg-foreground-500/50 hover:bg-foreground-300'
              }`}
            ></button>
          ))}
        </div>
        <span className="text-xs text-foreground-400 font-display tracking-widest">
          {String(index + 1).padStart(2, '0')} / {String(featured.length).padStart(2, '0')}
        </span>
      </div>
    </section>
  );
}